import type { AppConfig } from "@/config"
import { PRESET } from "@/features/presets/params"

export const PreloadByPreset = {
  [PRESET.FACE]: () => import("@/features/presets/face/preload"),
  [PRESET.FACE_180]: () => import("@/features/presets/face-180/preload"),
  [PRESET.HAIR]: () => import("@/features/presets/hair/preload"),
}

export const FEATURE = {
  TUTORIAL: "tutorial",
} as const

export type Feature = (typeof FEATURE)[keyof typeof FEATURE]

export const PreloadByFeature = {
  [FEATURE.TUTORIAL]: () => import("@/features/tutorial/preload"),
}

/**
 * Preloads preset's resources and features in advance. For more information, see [Preload Resources](/getting-started/advanced-usage#preload-liqa-resources-before-liqa-session) page.
 * @param options - Preload options.
 * @example
 * ```ts
 * // Preload a preset
 * preload({ preset: "face" })
 *
 * // Preload tutorial videos
 * preload({ feature: "tutorial" })
 *
 * // Preload both preset and tutorial
 * preload({ preset: "face", feature: "tutorial" })
 * ```
 * @category Misc
 */
export const preload = async (options: PreloadOptions = {}) => {
  const { preset = PRESET.FACE, feature } = options

  const promises: Promise<any>[] = []

  if (feature) {
    const featureLoader = PreloadByFeature[feature]
    if (!featureLoader) {
      console.warn(`Unknown feature "${feature}". Skipping feature preloading.`)
    } else {
      promises.push(featureLoader().then(({ preload }) => preload()))
    }
  }

  if (!feature || preset) {
    const presetLoader = PreloadByPreset[preset]
    if (!presetLoader) {
      console.warn(`Unknown preset "${preset}". Skipping preset preloading.`)
    } else {
      promises.push(presetLoader().then(({ preload }) => preload()))
    }
  }

  await Promise.all(promises)
}

/**
 * @category Misc
 */
export type PreloadOptions = Partial<Pick<AppConfig, "preset">> & {
  feature?: Feature
}
